// Sport-specific configurations for talent rating calculations

export interface SportStatConfig {
  sport: string;
  displayName: string;
  primaryStats: StatWeight[];
  secondaryStats: StatWeight[];
  positionMultipliers: Record<string, number>;
  leaguePrestige: Record<string, number>;
}

export interface StatWeight {
  key: string;
  displayName: string;
  weight: number;
  maxValue: number;
  higherIsBetter: boolean;
}

export const SPORT_CONFIGS: Record<string, SportStatConfig> = {
  football: {
    sport: 'football',
    displayName: 'Soccer',
    primaryStats: [
      { key: 'goals', displayName: 'Goals', weight: 0.25, maxValue: 50, higherIsBetter: true },
      { key: 'assists', displayName: 'Assists', weight: 0.20, maxValue: 30, higherIsBetter: true },
      { key: 'appearances', displayName: 'Appearances', weight: 0.15, maxValue: 60, higherIsBetter: true },
      { key: 'minutes', displayName: 'Minutes Played', weight: 0.10, maxValue: 5000, higherIsBetter: true },
    ],
    secondaryStats: [
      { key: 'rating', displayName: 'Average Rating', weight: 0.15, maxValue: 10, higherIsBetter: true },
      { key: 'passAccuracy', displayName: 'Pass Accuracy %', weight: 0.10, maxValue: 100, higherIsBetter: true },
      { key: 'cleanSheets', displayName: 'Clean Sheets', weight: 0.05, maxValue: 30, higherIsBetter: true },
    ],
    positionMultipliers: {
      'Attacker': 1.1,
      'Midfielder': 1.0,
      'Defender': 0.95,
      'Goalkeeper': 0.90,
    },
    leaguePrestige: {
      'Premier League': 1.0,
      'La Liga': 0.98,
      'Bundesliga': 0.95,
      'Serie A': 0.95,
      'Ligue 1': 0.90,
      'MLS': 0.75,
      'default': 0.70,
    },
  },

  nba: {
    sport: 'nba',
    displayName: 'NBA Basketball',
    primaryStats: [
      { key: 'points', displayName: 'Points', weight: 0.25, maxValue: 35, higherIsBetter: true },
      { key: 'rebounds', displayName: 'Rebounds', weight: 0.15, maxValue: 15, higherIsBetter: true },
      { key: 'assists', displayName: 'Assists', weight: 0.15, maxValue: 12, higherIsBetter: true },
      { key: 'games', displayName: 'Games Played', weight: 0.10, maxValue: 82, higherIsBetter: true },
    ],
    secondaryStats: [
      { key: 'fieldGoalPct', displayName: 'FG%', weight: 0.10, maxValue: 60, higherIsBetter: true },
      { key: 'steals', displayName: 'Steals', weight: 0.10, maxValue: 3, higherIsBetter: true },
      { key: 'blocks', displayName: 'Blocks', weight: 0.10, maxValue: 3, higherIsBetter: true },
      { key: 'plusMinus', displayName: '+/-', weight: 0.05, maxValue: 15, higherIsBetter: true },
    ],
    positionMultipliers: {
      'G': 1.05,
      'F': 1.0,
      'C': 0.95,
      'G-F': 1.02,
      'F-C': 0.98,
    },
    leaguePrestige: {
      'NBA': 1.0,
      'default': 0.80,
    },
  },

  basketball: {
    sport: 'basketball',
    displayName: 'Basketball',
    primaryStats: [
      { key: 'points', displayName: 'Points', weight: 0.25, maxValue: 30, higherIsBetter: true },
      { key: 'rebounds', displayName: 'Rebounds', weight: 0.15, maxValue: 12, higherIsBetter: true },
      { key: 'assists', displayName: 'Assists', weight: 0.15, maxValue: 10, higherIsBetter: true },
      { key: 'games', displayName: 'Games Played', weight: 0.10, maxValue: 50, higherIsBetter: true },
    ],
    secondaryStats: [
      { key: 'fieldGoalPct', displayName: 'FG%', weight: 0.10, maxValue: 55, higherIsBetter: true },
      { key: 'steals', displayName: 'Steals', weight: 0.10, maxValue: 2.5, higherIsBetter: true },
      { key: 'blocks', displayName: 'Blocks', weight: 0.10, maxValue: 2.5, higherIsBetter: true },
      { key: 'minutes', displayName: 'Minutes', weight: 0.05, maxValue: 40, higherIsBetter: true },
    ],
    positionMultipliers: {
      'Guard': 1.05,
      'Forward': 1.0,
      'Center': 0.95,
    },
    leaguePrestige: {
      'EuroLeague': 0.90,
      'default': 0.75,
    },
  },

  nfl: {
    sport: 'nfl',
    displayName: 'NFL Football',
    primaryStats: [
      { key: 'touchdowns', displayName: 'Touchdowns', weight: 0.25, maxValue: 50, higherIsBetter: true },
      { key: 'yards', displayName: 'Total Yards', weight: 0.20, maxValue: 5000, higherIsBetter: true },
      { key: 'games', displayName: 'Games Played', weight: 0.10, maxValue: 17, higherIsBetter: true },
      { key: 'completionPct', displayName: 'Completion %', weight: 0.15, maxValue: 75, higherIsBetter: true },
    ],
    secondaryStats: [
      { key: 'passerRating', displayName: 'Passer Rating', weight: 0.15, maxValue: 158.3, higherIsBetter: true },
      { key: 'interceptions', displayName: 'Interceptions', weight: 0.10, maxValue: 20, higherIsBetter: false },
      { key: 'sacks', displayName: 'Sacks (Defense)', weight: 0.05, maxValue: 20, higherIsBetter: true },
    ],
    positionMultipliers: {
      'QB': 1.15,
      'WR': 1.05,
      'RB': 1.0,
      'TE': 0.95,
      'OL': 0.90,
      'DL': 0.95,
      'LB': 0.95,
      'DB': 0.95,
      'K': 0.80,
      'P': 0.75,
    },
    leaguePrestige: {
      'NFL': 1.0,
      'default': 0.70,
    },
  },

  baseball: {
    sport: 'baseball',
    displayName: 'Baseball',
    primaryStats: [
      { key: 'battingAverage', displayName: 'Batting Average', weight: 0.20, maxValue: 0.400, higherIsBetter: true },
      { key: 'homeRuns', displayName: 'Home Runs', weight: 0.20, maxValue: 60, higherIsBetter: true },
      { key: 'rbi', displayName: 'RBIs', weight: 0.15, maxValue: 150, higherIsBetter: true },
      { key: 'games', displayName: 'Games Played', weight: 0.10, maxValue: 162, higherIsBetter: true },
    ],
    secondaryStats: [
      { key: 'ops', displayName: 'OPS', weight: 0.15, maxValue: 1.200, higherIsBetter: true },
      { key: 'stolenBases', displayName: 'Stolen Bases', weight: 0.10, maxValue: 60, higherIsBetter: true },
      { key: 'era', displayName: 'ERA (Pitchers)', weight: 0.10, maxValue: 5.0, higherIsBetter: false },
    ],
    positionMultipliers: {
      'P': 1.10,
      'C': 1.0,
      'SS': 1.0,
      '1B': 0.95,
      '2B': 0.95,
      '3B': 0.95,
      'OF': 0.95,
      'DH': 0.90,
    },
    leaguePrestige: {
      'MLB': 1.0,
      'NPB': 0.85,
      'default': 0.70,
    },
  },

  hockey: {
    sport: 'hockey',
    displayName: 'Ice Hockey',
    primaryStats: [
      { key: 'goals', displayName: 'Goals', weight: 0.25, maxValue: 60, higherIsBetter: true },
      { key: 'assists', displayName: 'Assists', weight: 0.20, maxValue: 80, higherIsBetter: true },
      { key: 'points', displayName: 'Points', weight: 0.15, maxValue: 130, higherIsBetter: true },
      { key: 'games', displayName: 'Games Played', weight: 0.10, maxValue: 82, higherIsBetter: true },
    ],
    secondaryStats: [
      { key: 'plusMinus', displayName: '+/-', weight: 0.10, maxValue: 50, higherIsBetter: true },
      { key: 'powerPlayGoals', displayName: 'PP Goals', weight: 0.10, maxValue: 25, higherIsBetter: true },
      { key: 'savePercentage', displayName: 'Save % (Goalies)', weight: 0.10, maxValue: 95, higherIsBetter: true },
    ],
    positionMultipliers: {
      'C': 1.05,
      'RW': 1.0,
      'LW': 1.0,
      'D': 0.95,
      'G': 1.10,
    },
    leaguePrestige: {
      'NHL': 1.0,
      'KHL': 0.85,
      'SHL': 0.80,
      'default': 0.70,
    },
  },

  mma: {
    sport: 'mma',
    displayName: 'MMA',
    primaryStats: [
      { key: 'wins', displayName: 'Wins', weight: 0.25, maxValue: 30, higherIsBetter: true },
      { key: 'winPercentage', displayName: 'Win %', weight: 0.20, maxValue: 100, higherIsBetter: true },
      { key: 'finishes', displayName: 'Finishes', weight: 0.20, maxValue: 25, higherIsBetter: true },
      { key: 'titleFights', displayName: 'Title Fights', weight: 0.15, maxValue: 10, higherIsBetter: true },
    ],
    secondaryStats: [
      { key: 'knockouts', displayName: 'Knockouts', weight: 0.10, maxValue: 20, higherIsBetter: true },
      { key: 'submissions', displayName: 'Submissions', weight: 0.05, maxValue: 15, higherIsBetter: true },
      { key: 'fightOfNight', displayName: 'FOTN Bonuses', weight: 0.05, maxValue: 10, higherIsBetter: true },
    ],
    positionMultipliers: {
      'Heavyweight': 1.0,
      'Light Heavyweight': 1.0,
      'Middleweight': 1.05,
      'Welterweight': 1.05,
      'Lightweight': 1.10,
      'Featherweight': 1.0,
      'Bantamweight': 0.95,
      'Flyweight': 0.90,
    },
    leaguePrestige: {
      'UFC': 1.0,
      'Bellator': 0.85,
      'ONE': 0.80,
      'PFL': 0.75,
      'default': 0.60,
    },
  },

  rugby: {
    sport: 'rugby',
    displayName: 'Rugby',
    primaryStats: [
      { key: 'tries', displayName: 'Tries', weight: 0.25, maxValue: 20, higherIsBetter: true },
      { key: 'tackles', displayName: 'Tackles', weight: 0.20, maxValue: 200, higherIsBetter: true },
      { key: 'carries', displayName: 'Carries', weight: 0.15, maxValue: 150, higherIsBetter: true },
      { key: 'games', displayName: 'Games Played', weight: 0.10, maxValue: 30, higherIsBetter: true },
    ],
    secondaryStats: [
      { key: 'metersGained', displayName: 'Meters Gained', weight: 0.15, maxValue: 1000, higherIsBetter: true },
      { key: 'linebreaks', displayName: 'Linebreaks', weight: 0.10, maxValue: 30, higherIsBetter: true },
      { key: 'turnovers', displayName: 'Turnovers Won', weight: 0.05, maxValue: 20, higherIsBetter: true },
    ],
    positionMultipliers: {
      'Fullback': 1.05,
      'Wing': 1.0,
      'Centre': 1.0,
      'Fly-half': 1.10,
      'Scrum-half': 1.05,
      'Number 8': 1.0,
      'Flanker': 0.95,
      'Lock': 0.90,
      'Prop': 0.85,
      'Hooker': 0.90,
    },
    leaguePrestige: {
      'Six Nations': 1.0,
      'Rugby Championship': 1.0,
      'Premiership': 0.90,
      'Top 14': 0.90,
      'Super Rugby': 0.85,
      'default': 0.70,
    },
  },

  'formula-1': {
    sport: 'formula-1',
    displayName: 'Formula 1',
    primaryStats: [
      { key: 'wins', displayName: 'Race Wins', weight: 0.30, maxValue: 15, higherIsBetter: true },
      { key: 'podiums', displayName: 'Podiums', weight: 0.20, maxValue: 20, higherIsBetter: true },
      { key: 'points', displayName: 'Championship Points', weight: 0.20, maxValue: 500, higherIsBetter: true },
      { key: 'polePositions', displayName: 'Pole Positions', weight: 0.15, maxValue: 15, higherIsBetter: true },
    ],
    secondaryStats: [
      { key: 'fastestLaps', displayName: 'Fastest Laps', weight: 0.10, maxValue: 10, higherIsBetter: true },
      { key: 'dnfRate', displayName: 'DNF Rate', weight: 0.05, maxValue: 50, higherIsBetter: false },
    ],
    positionMultipliers: {
      'Driver': 1.0,
    },
    leaguePrestige: {
      'Formula 1': 1.0,
      'Formula 2': 0.70,
      'Formula 3': 0.50,
      'IndyCar': 0.75,
      'default': 0.50,
    },
  },

  afl: {
    sport: 'afl',
    displayName: 'AFL',
    primaryStats: [
      { key: 'disposals', displayName: 'Disposals', weight: 0.25, maxValue: 35, higherIsBetter: true },
      { key: 'goals', displayName: 'Goals', weight: 0.20, maxValue: 80, higherIsBetter: true },
      { key: 'marks', displayName: 'Marks', weight: 0.15, maxValue: 12, higherIsBetter: true },
      { key: 'games', displayName: 'Games Played', weight: 0.10, maxValue: 25, higherIsBetter: true },
    ],
    secondaryStats: [
      { key: 'tackles', displayName: 'Tackles', weight: 0.15, maxValue: 8, higherIsBetter: true },
      { key: 'clearances', displayName: 'Clearances', weight: 0.10, maxValue: 8, higherIsBetter: true },
      { key: 'contestedPossessions', displayName: 'Contested Possessions', weight: 0.05, maxValue: 15, higherIsBetter: true },
    ],
    positionMultipliers: {
      'Forward': 1.05,
      'Midfielder': 1.10,
      'Defender': 0.95,
      'Ruck': 0.90,
    },
    leaguePrestige: {
      'AFL': 1.0,
      'VFL': 0.70,
      'default': 0.60,
    },
  },

  handball: {
    sport: 'handball',
    displayName: 'Handball',
    primaryStats: [
      { key: 'goals', displayName: 'Goals', weight: 0.30, maxValue: 150, higherIsBetter: true },
      { key: 'assists', displayName: 'Assists', weight: 0.20, maxValue: 80, higherIsBetter: true },
      { key: 'games', displayName: 'Games Played', weight: 0.15, maxValue: 40, higherIsBetter: true },
    ],
    secondaryStats: [
      { key: 'shootingPct', displayName: 'Shooting %', weight: 0.15, maxValue: 70, higherIsBetter: true },
      { key: 'saves', displayName: 'Saves (Goalkeepers)', weight: 0.10, maxValue: 300, higherIsBetter: true },
      { key: 'steals', displayName: 'Steals', weight: 0.10, maxValue: 50, higherIsBetter: true },
    ],
    positionMultipliers: {
      'Left Back': 1.05,
      'Right Back': 1.05,
      'Centre Back': 1.10,
      'Pivot': 1.0,
      'Left Wing': 0.95,
      'Right Wing': 0.95,
      'Goalkeeper': 1.0,
    },
    leaguePrestige: {
      'EHF Champions League': 1.0,
      'Bundesliga': 0.95,
      'LNH': 0.90,
      'default': 0.70,
    },
  },

  volleyball: {
    sport: 'volleyball',
    displayName: 'Volleyball',
    primaryStats: [
      { key: 'kills', displayName: 'Kills', weight: 0.25, maxValue: 500, higherIsBetter: true },
      { key: 'aces', displayName: 'Aces', weight: 0.15, maxValue: 50, higherIsBetter: true },
      { key: 'blocks', displayName: 'Blocks', weight: 0.20, maxValue: 150, higherIsBetter: true },
      { key: 'digs', displayName: 'Digs', weight: 0.10, maxValue: 300, higherIsBetter: true },
    ],
    secondaryStats: [
      { key: 'attackPct', displayName: 'Attack %', weight: 0.15, maxValue: 50, higherIsBetter: true },
      { key: 'assists', displayName: 'Assists', weight: 0.10, maxValue: 1000, higherIsBetter: true },
      { key: 'serves', displayName: 'Service Points', weight: 0.05, maxValue: 100, higherIsBetter: true },
    ],
    positionMultipliers: {
      'Outside Hitter': 1.05,
      'Opposite': 1.05,
      'Middle Blocker': 1.0,
      'Setter': 1.10,
      'Libero': 0.95,
    },
    leaguePrestige: {
      'FIVB World Championship': 1.0,
      'CEV Champions League': 0.95,
      'Serie A': 0.90,
      'default': 0.70,
    },
  },
};

export const getSportConfig = (sport: string): SportStatConfig => {
  const normalizedSport = sport.toLowerCase().replace(/\s+/g, '-');
  return SPORT_CONFIGS[normalizedSport] || SPORT_CONFIGS['football'];
};

export const getAllSports = (): string[] => {
  return Object.keys(SPORT_CONFIGS);
};
